import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { MessageSquare, Send, Loader2, Plus, ChevronRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { logger } from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categories = [
  { value: 'billing', label: 'Billing' },
  { value: 'extension', label: 'Extension issue' },
  { value: 'feature', label: 'Feature request' },
  { value: 'bug', label: 'Bug report' },
  { value: 'other', label: 'Other' },
];

const statusColors = {
  open: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  in_progress: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  resolved: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
};

export default function SupportPage() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newTicketOpen, setNewTicketOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');

  const [newTicket, setNewTicket] = useState({
    subject: '',
    category: 'other',
    message: '',
  });

  const fetchTickets = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/support/tickets`, {
        withCredentials: true
      });
      // API returns {tickets: [...]} so extract the array
      const ticketsData = response.data?.tickets || response.data || [];
      setTickets(Array.isArray(ticketsData) ? ticketsData : []);
    } catch (err) { 
      logger.error("Operation failed:", err);
      setTickets([]); // Ensure tickets is always an array on error
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const fetchTicketDetails = async (ticketId) => {
    try {
      const response = await axios.get(`${API_URL}/api/support/tickets/${ticketId}`, {
        withCredentials: true
      });
      setSelectedTicket(response.data);
    } catch (err) { logger.error("Operation failed:", err); }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/support/tickets`, newTicket, {
        withCredentials: true
      });
      setNewTicketOpen(false);
      setNewTicket({ subject: '', category: 'other', message: '' });
      fetchTickets();
    } catch (err) { logger.error("Operation failed:", err); } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyMessage.trim() || !selectedTicket) return;
    
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/support/tickets/${selectedTicket.id}/messages`, 
        { message: replyMessage },
        { withCredentials: true }
      );
      setReplyMessage('');
      fetchTicketDetails(selectedTicket.id);
    } catch (err) { logger.error("Operation failed:", err); } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="support-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-space text-2xl md:text-3xl text-white mb-2">Support</h1>
          <p className="text-white/55">Get help with your SendPI account</p>
        </div>
        <Dialog open={newTicketOpen} onOpenChange={setNewTicketOpen}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-cyan-500 to-purple-500 hover:opacity-90 text-white rounded-full" data-testid="new-ticket-btn">
              <Plus className="w-4 h-4 mr-2" />
              New ticket
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0E1830] border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white">Create Support Ticket</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateTicket} className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label className="text-white/70">Category</Label>
                <Select 
                  value={newTicket.category}
                  onValueChange={(v) => setNewTicket(s => ({ ...s, category: v }))}
                >
                  <SelectTrigger className="bg-white/5 border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0E1830] border-white/10">
                    {categories.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value} className="text-white hover:bg-white/5">
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-white/70">Subject</Label>
                <Input
                  value={newTicket.subject}
                  onChange={(e) => setNewTicket(s => ({ ...s, subject: e.target.value }))}
                  placeholder="Brief description of your issue"
                  required
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/25"
                  data-testid="ticket-subject-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-white/70">Message</Label>
                <Textarea
                  value={newTicket.message}
                  onChange={(e) => setNewTicket(s => ({ ...s, message: e.target.value }))}
                  placeholder="Describe your issue in detail..."
                  required
                  rows={5}
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/25 resize-none"
                  data-testid="ticket-message-input"
                />
              </div>

              <Button 
                type="submit" 
                disabled={submitting}
                className="w-full bg-gradient-to-r from-cyan-500 to-purple-500 hover:opacity-90 text-white rounded-xl"
                data-testid="submit-ticket-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send message
                  </>
                )}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {/* Tickets List */}
        <div className="md:col-span-1 space-y-4">
          <h3 className="text-white font-medium">Your Tickets</h3>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 text-white/40 animate-spin" />
            </div>
          ) : tickets.length === 0 ? (
            <div className="bg-[#0E1830] border border-white/5 rounded-xl p-6 text-center">
              <MessageSquare className="w-8 h-8 text-white/20 mx-auto mb-3" />
              <p className="text-white/55 text-sm">No tickets yet</p>
              <p className="text-white/40 text-xs">Create a ticket to get help</p>
            </div>
          ) : (
            <div className="space-y-2">
              {tickets.map((ticket) => (
                <button
                  key={ticket.id}
                  onClick={() => fetchTicketDetails(ticket.id)}
                  className={`w-full text-left p-4 rounded-xl border transition-colors ${
                    selectedTicket?.id === ticket.id
                      ? 'bg-white/5 border-cyan-500/30'
                      : 'bg-[#0E1830] border-white/5 hover:border-white/10'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className={`px-2 py-0.5 text-xs rounded-full border ${statusColors[ticket.status]}`}>
                      {ticket.status.replace('_', ' ')}
                    </span>
                    <ChevronRight className="w-4 h-4 text-white/40" />
                  </div>
                  <p className="text-white text-sm font-medium truncate">{ticket.subject}</p>
                  <p className="text-white/40 text-xs mt-1">
                    {new Date(ticket.created_at).toLocaleDateString()}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Ticket Details */}
        <div className="md:col-span-2">
          {selectedTicket ? (
            <div className="bg-[#0E1830] border border-white/5 rounded-2xl p-6">
              <div className="mb-6 pb-6 border-b border-white/10">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-white font-medium">{selectedTicket.subject}</h3>
                  <span className={`px-2 py-0.5 text-xs rounded-full border ${statusColors[selectedTicket.status]}`}>
                    {selectedTicket.status.replace('_', ' ')}
                  </span>
                </div>
                <p className="text-white/40 text-sm">
                  Created {new Date(selectedTicket.created_at).toLocaleDateString()}
                </p>
              </div>

              {/* Messages */}
              <div className="space-y-4 mb-6 max-h-96 overflow-y-auto">
                {selectedTicket.messages?.map((msg, i) => (
                  <div 
                    key={msg.id || `msg-${msg.created_at}`}
                    className={`p-4 rounded-xl ${
                      msg.author === 'user' 
                        ? 'bg-cyan-500/10 border border-cyan-500/20 ml-8' 
                        : 'bg-white/5 border border-white/10 mr-8'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-xs ${msg.author === 'user' ? 'text-cyan-400' : 'text-white/55'}`}>
                        {msg.author === 'user' ? 'You' : 'Support'}
                      </span>
                      <span className="text-white/40 text-xs">
                        {new Date(msg.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-white/70 text-sm whitespace-pre-wrap">{msg.message}</p>
                  </div>
                ))}
              </div>

              {/* Reply Form */}
              {selectedTicket.status !== 'resolved' && (
                <form onSubmit={handleReply} className="flex gap-3">
                  <Input
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    placeholder="Type your reply..."
                    className="flex-1 bg-white/5 border-white/10 text-white placeholder:text-white/25"
                    data-testid="reply-input"
                  />
                  <Button 
                    type="submit"
                    disabled={submitting || !replyMessage.trim()}
                    className="bg-gradient-to-r from-cyan-500 to-purple-500 hover:opacity-90 text-white rounded-xl"
                    data-testid="send-reply-btn"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </form>
              )}
            </div>
          ) : (
            <div className="bg-[#0E1830] border border-white/5 rounded-2xl p-12 text-center">
              <MessageSquare className="w-12 h-12 text-white/20 mx-auto mb-4" />
              <p className="text-white/55">Select a ticket to view details</p>
            </div>
          )}
        </div>
      </div>

      {/* Response time note */}
      <p className="text-white/40 text-xs text-center">
        We typically respond within 24 hours (usually much faster)
      </p>
    </div>
  );
}
